import bowser from 'bowser'
import React from 'react'
import FocusLock from 'react-focus-lock'
import { useTranslation } from 'react-i18next'
import { RemoveScroll } from 'react-remove-scroll'
import styled from 'styled-components'
import Modal, { BaseModalBackground } from 'styled-react-modal'
import { Logo, LogoTheme } from '../common/logo'
import { CustomColor } from '../common/style/color'
import { CustomButton } from '../common/style/components/button'
import { Image } from '../common/style/components/image'
import { mobile } from '../common/style/media-queries'
import { SRSpan } from '../common/typography'
import BackArrowIcon from './combined-shape.svg'

declare module 'styled-react-modal' {
  interface ModalProps {
    role?: string
    'aria-labelledby'?: string
    tabindex?: string
  }
}

interface CustomModalProps {
  isOpen: boolean
  onClose: () => void
  illustration?: string
  title?: string
}

export const CustomModal: React.FC<CustomModalProps> = ({
  isOpen,
  onClose,
  children,
  illustration,
  title,
}) => {
  const [t] = useTranslation('modal')

  return (
    <ModalWrapper
      isOpen={isOpen}
      onBackgroundClick={onClose}
      onEscapeKeydown={onClose}
      role="dialog"
      aria-labelledby="modal-title"
      tabindex="-1"
    >
      <div role="document">
        <RemoveScroll>
          <FocusLock>
            {bowser.mobile ? (
              <Header>
                <Image onClick={onClose} src={BackArrowIcon} alt={t('back')} />
                <StyledLogo logoTheme={LogoTheme.DARK} />
              </Header>
            ) : (
              <Close onClick={onClose} />
            )}
            <ContentWrapper>
              {illustration && <Illustration src={illustration} />}
              {title && <Title id="modal-title">{title}</Title>}
              {children}
            </ContentWrapper>
          </FocusLock>
        </RemoveScroll>
      </div>
    </ModalWrapper>
  )
}

export const ModalWrapper = Modal.styled`
  position: relative;
  max-height: 80%;
  overflow: auto;
  @media ${mobile} {
    max-height: none;
    height: 100%;
  border-radius: 0px;
}
  width: 400px;
  border-radius: 5px;
  background-color: #ffffff;
  padding: 20px;
  box-sizing: border-box;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #33455b;
`

const Illustration = styled(Image)`
  margin-top: 16px;
`
const Title = styled.h1`
  font-size: 22px;
  margin: 18px 60px 10px 60px;
  text-align: center;
  line-height: normal;
`

// without the "any" a circular reference is detected which does not seem relevant
export const ModalBackground = styled(BaseModalBackground as any)`
  z-index: 1600;
  background-color: rgba(130, 178, 255, 0.9);

  @media ${mobile} {
    width: 100%;
    height: 100%;
  }
`
const Header = styled.div`
  display: flex;
  align-items: center;
  box-shadow: 0 1px 1px 0 rgba(51, 69, 91, 0.1);
`

const StyledLogo = styled(Logo)`
  display: flex;
  margin: auto;
`

const Close: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [t] = useTranslation('modal')
  return (
    <CloseWrapper onClick={onClick}>
      <SRSpan>{t('close')}</SRSpan>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="26"
        viewBox="0 0 25 26"
        aria-hidden="true"
      >
        <g fill="none" fillRule="evenodd" stroke={CustomColor.grey}>
          <path
            d="M.638.448L24.275 24.078M24.426.278L.273 24.304"
            transform="translate(0 .5)"
          />
        </g>
      </svg>
    </CloseWrapper>
  )
}

const CloseWrapper = styled(CustomButton)`
  position: absolute;
  top: 20px;
  right: 20px;
`
